/** @jsx jsx */
import { css, jsx } from "@emotion/core"

import React from "react"
import { ThemeProvider as CD2ThemeProvider } from "@cd2/theme"

const absoluteColors = {
  blue: "#276FBF",
}

export const colors = {
  primary: absoluteColors.blue,
  secondary: "#f9ca24",
  default: "rgba(0,0,0,0.83)",
  text: "rgba(0,0,0,0.83)",
  formBorderColor: "#DADADA",
  neutral: "#065ed5",
  ...absoluteColors,
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  return (
    <CD2ThemeProvider
      theme={{
        fontFamily: "'Funnel Sans', sans-serif",
        colors,
        borderRadius: 8,
        forms: {
          border: {
            focusColor: colors.primary,
            color: colors.formBorderColor,
          },
          padding: { topAndBottom: 11.5, sides: 12 },
        },
        text: {
          colors,
          ratios: { "1": 2.25, "2": 1.66, "3": 1.25, "4": 1, p: 1 },
          sizes: { large: 125, normal: 100, small: 87.5 },
          lineHeight: 1.5,
          blockSpacing: 0.5,
          groups: {
            bodyColor: "rgba(0,0,0,0.5)",
            preColor: "primary",
          },
        },
        alert: {
          padding: "16px 32px",
          boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
        },
        sidebar: {
          colors: {
            background: "white",
            links: "black",
          },
          boxShadow: "1px 0 1px rgba(0,0,0,0.4)",
        },
        topBar: {
          color: "#ffda4d",
        },
        button: {
          boxShadow: "0 0.2rem 0.5rem rgba(103,110,144,.2), 0 0 0 0.1rem rgba(103,110,144,.05)",
        },
      }}
      globalStyles={css`
        body {
          -webkit-font-smoothing: antialiased;
        }
        img {
          max-width: 100%;
          display: block;
        }
        a {
          color: ${colors.primary};
          text-decoration: none;
        }
        ul {
          margin: 0;
          padding: 0 0 0 18px;
          color: rgba(0, 0, 0, 0.83);
          line-height: 1.5;
        }
        h1, h2, h3, h4, h5, h6 {
          font-family: 'Funnel Display', sans-serif;
        }
        .data-table {
          width: 100%;
          border-collapse: separate;
          border-spacing: 0;
          background: white;
          border-radius: 12px;
          overflow: hidden;
          border: 1px solid #e5e5e5;
        }
        .data-table-empty {
          padding: 48px 24px;
          text-align: center;
          color: rgba(0, 0, 0, 0.5);
          background: white;
          border-radius: 12px;
          border: 1px solid #e5e5e5;
        }
        .data-table-empty p {
          margin: 0;
          font-size: 14px;
        }
        .data-table-header {
          padding: 16px 20px;
          text-align: left;
          font-weight: 600;
          font-size: 13px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: rgba(0, 0, 0, 0.6);
          background: #fafafa;
          border-bottom: 1px solid #e5e5e5;
          white-space: nowrap;
        }
        .data-table-header:first-child {
          padding-left: 24px;
        }
        .data-table-header:last-child {
          padding-right: 24px;
        }
        .data-table-header-sortable {
          cursor: pointer;
          user-select: none;
          transition: background-color 0.15s ease, color 0.15s ease;
        }
        .data-table-header-sortable:hover {
          background: #f0f0f0;
          color: rgba(0, 0, 0, 0.8);
        }
        .data-table-row {
          transition: background-color 0.15s ease;
          border-bottom: 1px solid #f0f0f0;
        }
        .data-table-row:last-child {
          border-bottom: none;
        }
        .data-table-row:hover {
          background: #fafafa;
        }
        .data-table-cell {
          padding: 18px 20px;
          font-size: 14px;
          color: rgba(0, 0, 0, 0.85);
          vertical-align: middle;
        }
        .data-table-row:first-child .data-table-cell:first-child {
          border-top-left-radius: 12px;
        }
        .data-table-row:first-child .data-table-cell:last-child {
          border-top-right-radius: 12px;
        }
        .data-table-row:last-child .data-table-cell:first-child {
          border-bottom-left-radius: 12px;
        }
        .data-table-row:last-child .data-table-cell:last-child {
          border-bottom-right-radius: 12px;
        }
        .data-table-cell:first-child {
          padding-left: 24px;
        }
        .data-table-cell:last-child {
          padding-right: 24px;
        }
        .data-table-cell-primary {
          padding: 18px 20px;
        }
        .data-table-cell-primary:first-child {
          padding-left: 24px;
        }
        .data-table-cell-primary-content {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .data-table-wrapper {
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
          width: 100%;
        }
        .data-table-wrapper .data-table-cell-primary-content > div:first-child {
          flex-shrink: 0;
        }
        .data-table-cards {
          display: none;
          flex-direction: column;
          gap: 12px;
        }
        .data-table-card {
          background: white;
          border: 1px solid #e5e5e5;
          border-radius: 12px;
          overflow: hidden;
          transition: background-color 0.15s ease;
        }
        .data-table-card:hover {
          background: #fafafa;
        }
        .data-table-card-header {
          padding: 16px;
          border-bottom: 1px solid #f0f0f0;
        }
        .data-table-card-body {
          padding: 12px 16px;
          display: flex;
          flex-direction: column;
          gap: 10px;
        }
        .data-table-card-row {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 12px;
        }
        .data-table-card-label {
          font-size: 12px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: rgba(0, 0, 0, 0.6);
          white-space: nowrap;
          flex-shrink: 0;
        }
        .data-table-card-value {
          font-size: 14px;
          color: rgba(0, 0, 0, 0.85);
          text-align: right;
          flex: 1;
          word-break: break-word;
        }
        .data-table-card-value .data-table-users {
          justify-content: flex-end;
          text-align: right;
        }
        .data-table-card-value .data-table-users a {
          color: rgba(0, 0, 0, 0.7);
          text-decoration: none;
          transition: color 0.15s ease;
        }
        .data-table-card-value .data-table-users a:hover {
          color: ${colors.primary};
          text-decoration: underline;
        }
        .data-table-link-primary {
          color: ${colors.primary};
          text-decoration: underline;
          font-weight: 600;
          transition: color 0.15s ease;
        }
        .data-table-link-primary:hover {
          color: #1a5aa3;
        }
        .data-table-users {
          display: flex;
          flex-wrap: wrap;
          gap: 4px;
        }
        .data-table-users a {
          color: rgba(0, 0, 0, 0.7);
          text-decoration: none;
          transition: color 0.15s ease;
        }
        .data-table-users a:hover {
          color: ${colors.primary};
          text-decoration: underline;
        }
        .data-table-cell-number {
          font-variant-numeric: tabular-nums;
          color: rgba(0, 0, 0, 0.7);
        }
        .data-table-cell-date {
          color: rgba(0, 0, 0, 0.6);
          font-size: 13px;
        }
        .data-table-controls {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 16px;
          margin-bottom: 24px;
          padding: 16px 20px;
          background: white;
          border-radius: 12px;
          border: 1px solid #e5e5e5;
        }
        .data-table-controls-left {
          display: flex;
          align-items: center;
          gap: 12px;
          flex: 1;
          min-width: 200px;
        }
        .data-table-controls-right {
          display: flex;
          align-items: center;
          gap: 16px;
          flex-wrap: wrap;
        }
        .data-table-search {
          flex: 1;
          min-width: 200px;
          padding: 10px 14px;
          font-size: 16px;
          border: 1px solid #e5e5e5;
          border-radius: 8px;
          background: white;
          color: rgba(0, 0, 0, 0.85);
          transition: border-color 0.15s ease, box-shadow 0.15s ease;
        }
        .data-table-search:focus {
          outline: none;
          border-color: ${colors.primary};
          box-shadow: 0 0 0 3px rgba(39, 111, 191, 0.1);
        }
        .data-table-search::placeholder {
          color: rgba(0, 0, 0, 0.4);
        }
        .data-table-scope {
          padding: 10px 14px;
          font-size: 14px;
          border: 1px solid #e5e5e5;
          border-radius: 8px;
          background: white;
          color: rgba(0, 0, 0, 0.85);
          cursor: pointer;
          transition: border-color 0.15s ease, box-shadow 0.15s ease;
        }
        .data-table-scope:focus {
          outline: none;
          border-color: ${colors.primary};
          box-shadow: 0 0 0 3px rgba(39, 111, 191, 0.1);
        }
        .data-table-results-count {
          font-size: 13px;
          color: rgba(0, 0, 0, 0.6);
          white-space: nowrap;
        }
        .data-table-pagination {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .data-table-pagination-button {
          padding: 8px 14px;
          font-size: 13px;
          font-weight: 500;
          color: rgba(0, 0, 0, 0.7);
          background: white;
          border: 1px solid #e5e5e5;
          border-radius: 6px;
          cursor: pointer;
          transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
        }
        .data-table-pagination-button:hover:not(:disabled) {
          background: #fafafa;
          border-color: #d0d0d0;
          color: rgba(0, 0, 0, 0.85);
        }
        .data-table-pagination-button:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }
        .data-table-pagination-pages {
          display: flex;
          align-items: center;
          gap: 4px;
        }
        .data-table-pagination-page {
          min-width: 32px;
          height: 32px;
          padding: 0 8px;
          font-size: 13px;
          font-weight: 500;
          color: rgba(0, 0, 0, 0.7);
          background: white;
          border: 1px solid #e5e5e5;
          border-radius: 6px;
          cursor: pointer;
          transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
        }
        .data-table-pagination-page:hover {
          background: #fafafa;
          border-color: #d0d0d0;
          color: rgba(0, 0, 0, 0.85);
        }
        .data-table-pagination-page-active {
          background: ${colors.primary};
          border-color: ${colors.primary};
          color: white;
        }
        .data-table-pagination-page-active:hover {
          background: #1a5aa3;
          border-color: #1a5aa3;
          color: white;
        }
        .data-table-pagination-ellipsis {
          padding: 0 4px;
          color: rgba(0, 0, 0, 0.4);
          font-size: 13px;
        }
        @media (max-width: 768px) {
          .data-table-wrapper {
            display: none;
          }
          .data-table-cards {
            display: flex;
          }
          .data-table-card-header {
            padding: 14px;
          }
          .data-table-card-body {
            padding: 10px 14px;
            gap: 8px;
          }
          .data-table-card-row {
            gap: 10px;
          }
          .data-table-card-label {
            font-size: 11px;
          }
          .data-table-card-value {
            font-size: 13px;
          }
          .data-table-empty {
            padding: 32px 16px;
            border-radius: 8px;
          }
          .data-table-controls {
            flex-direction: column;
            align-items: stretch;
            gap: 12px;
            padding: 12px 16px;
            border-radius: 8px;
          }
          .data-table-controls-left {
            flex-direction: column;
            width: 100%;
            gap: 10px;
          }
          .data-table-search {
            width: 100%;
            min-width: 0;
            padding: 10px 12px;
            font-size: 14px;
          }
          .data-table-scope {
            width: 100%;
            padding: 10px 12px;
            font-size: 14px;
          }
          .data-table-controls-right {
            flex-direction: column;
            align-items: stretch;
            width: 100%;
            gap: 12px;
          }
          .data-table-results-count {
            font-size: 12px;
            text-align: center;
            white-space: normal;
          }
          .data-table-pagination {
            justify-content: center;
            flex-wrap: wrap;
            gap: 6px;
          }
          .data-table-pagination-button {
            padding: 8px 12px;
            font-size: 12px;
            flex: 1;
            min-width: 80px;
          }
          .data-table-pagination-pages {
            gap: 3px;
          }
          .data-table-pagination-page {
            min-width: 28px;
            height: 28px;
            padding: 0 6px;
            font-size: 12px;
          }
          .data-table-pagination-ellipsis {
            font-size: 12px;
          }
        }
        @media (max-width: 480px) {
          .data-table-cards {
            gap: 10px;
          }
          .data-table-card {
            border-radius: 8px;
          }
          .data-table-card-header {
            padding: 12px;
          }
          .data-table-card-body {
            padding: 8px 12px;
            gap: 6px;
          }
          .data-table-card-row {
            gap: 8px;
          }
          .data-table-card-label {
            font-size: 10px;
          }
          .data-table-card-value {
            font-size: 12px;
          }
          .data-table-pagination-button {
            padding: 7px 10px;
            font-size: 11px;
            min-width: 70px;
          }
          .data-table-pagination-page {
            min-width: 26px;
            height: 26px;
            padding: 0 4px;
            font-size: 11px;
          }
        }
      `}
    >
      {children}
    </CD2ThemeProvider>
  )
}
